import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { FilterKind, FilterLibrary, FilterOverview, PendingTag, RunScope } from '@shared/services'
import {
  readReviewCounts,
  actOnPendingTag,
  approveAllPending,
  nextFilterPictures,
  processFilterPictures,
  readFilterOverview,
  startFilterRun,
  undoFilterRun,
  updateFilterRun,
} from '@shared/services'
import { useRemembered } from '../useRemembered'
import { AutoFilterPilot } from './AutoFilterPilot'
import { AutoFilterDescribe } from './AutoFilterDescribe'
import './autoFilter.css'

/**
 * Auto Filter (Huanrui, 2026-10-05; Design Works 2026-10-08)
 * -------------------------------------------------------------
 * The spec is the project doc wonderworks-auto-filter-spec.md. A switch at
 * the top chooses the library: Audit (five categories) or Design Works
 * (Format only, with Audit's format tags; drawn a shade lighter so the two
 * are never mistaken). Top to bottom:
 *
 *  1. RUN: how much of the library is filtered, Try 50 / Run, progress;
 *     beside it the latest runs, each of which can be undone.
 *  2. A line to the Review Queue (2026-10-15), where the reported pictures
 *     and the trash now are (reviewSections.tsx), with possible duplicates.
 *  3. PENDING TAGS, folded away when not wanted: names the AI proposed that
 *     wait for their threshold (Brand 5, the others 20).
 *  4. The pilot (Audit only), folded: it compares without writing.
 *
 * The page drives a run: it asks the Worker for the next pictures and hands
 * them over five at a time, several requests at once. Closing the tab stops
 * the run where it is; Run again carries on with what is left.
 */

/** Requests in flight at once, five pictures each. */
const IN_FLIGHT = 8
/** Pictures asked for at a time. */
const PAGE = 40
/** Pending tags a column shows before "Show all". */
const COLUMN_FIRST = 4

const LABELS: Record<FilterKind, string> = {
  type: 'Type',
  format: 'Format',
  material: 'Material',
  brand: 'Brand',
  product: 'Product',
  industry: 'Industry',
  other: 'Other',
}

const LIBRARY_NAMES: Record<FilterLibrary, string> = {
  audit: 'Audit',
  'design-works': 'Design References',
  'visual-language': 'Visual Language',
}

const usd = (value: number) => (value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`)
const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

interface Progress {
  runId: string
  total: number
  done: number
  failed: number
  skipped: number
  added: number
  costUsd: number
  startedAt: number
}

export function AutoFilterPage() {
  const [library, setLibrary] = useRemembered<FilterLibrary>('ww.autoFilter.library', 'audit')
  const shownLibrary: FilterLibrary =
    library === 'design-works' || library === 'visual-language' ? library : 'audit'
  const [overview, setOverview] = useState<FilterOverview | null>(null)
  const [error, setError] = useState('')
  const [scope, setScope] = useState<RunScope>('unfiltered')
  const [progress, setProgress] = useState<Progress | null>(null)
  const [running, setRunning] = useState(false)
  const stop = useRef(false)

  const reload = useCallback(() => {
    readFilterOverview(shownLibrary)
      .then((found) => {
        // An answer for the library that was showing before the switch is dropped.
        if (found.library && found.library !== shownLibrary) return
        setOverview(found)
        setError('')
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read Auto Filter'))
  }, [shownLibrary])
  useEffect(() => {
    setOverview(null)
    setProgress(null)
    reload()
  }, [reload])

  const notYet = overview ? Math.max(0, overview.eligible - overview.filtered) : 0

  const run = useCallback(
    async (limit: number | null) => {
      if (!overview) return
      setError('')
      setRunning(true)
      stop.current = false
      const total = Math.min(limit ?? Infinity, scope === 'all' ? overview.eligible : notYet)
      let state: Progress | null = null
      try {
        const { runId } = await startFilterRun(scope, total, shownLibrary)
        state = { runId, total, done: 0, failed: 0, skipped: 0, added: 0, costUsd: 0, startedAt: Date.now() }
        setProgress({ ...state })
        let after = ''
        let handed = 0
        for (;;) {
          if (stop.current || handed >= total) break
          const page = await nextFilterPictures(scope, after, Math.min(PAGE, total - handed), shownLibrary)
          after = page.after
          handed += page.assetIds.length
          // Five at a time, several requests at once.
          const chunks: string[][] = []
          for (let i = 0; i < page.assetIds.length; i += 5) chunks.push(page.assetIds.slice(i, i + 5))
          let next = 0
          const worker = async () => {
            while (next < chunks.length && !stop.current) {
              const chunk = chunks[next++]!
              try {
                const outcomes = await processFilterPictures(runId, chunk)
                for (const one of outcomes) {
                  state!.costUsd += one.costUsd
                  state!.added += one.added
                  if (one.status === 'done') state!.done += 1
                  else if (one.status === 'skipped') state!.skipped += 1
                  else state!.failed += 1
                }
              } catch {
                state!.failed += chunk.length
              }
              setProgress({ ...state! })
            }
          }
          await Promise.all(Array.from({ length: Math.min(IN_FLIGHT, chunks.length) }, worker))
          await updateFilterRun(runId, { done: state.done, failed: state.failed, costUsd: state.costUsd }).catch(() => {})
          if (page.end) break
        }
        await updateFilterRun(runId, {
          done: state.done,
          failed: state.failed,
          costUsd: state.costUsd,
          status: stop.current ? 'stopped' : 'done',
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The run stopped')
        if (state) {
          await updateFilterRun(state.runId, {
            done: state.done,
            failed: state.failed,
            costUsd: state.costUsd,
            status: 'stopped',
          }).catch(() => {})
        }
      } finally {
        setRunning(false)
        reload()
      }
    },
    [overview, scope, notYet, reload, shownLibrary],
  )

  const elapsed = progress ? (Date.now() - progress.startedAt) / 1000 : 0
  const handledCount = progress ? progress.done + progress.failed + progress.skipped : 0
  const perMinute = elapsed > 5 ? (handledCount / elapsed) * 60 : 0
  const left = progress && perMinute > 0 ? Math.max(0, progress.total - handledCount) / perMinute : 0
  const works = shownLibrary === 'design-works'
  const name = LIBRARY_NAMES[shownLibrary]
  // docs/ai-tags-off.sql: tags are written by people, and no run starts.
  const tagsOff = overview?.aiTagsOn === false

  return (
    <div className={`afl${works ? ' afl--dw' : ''}`}>
      <header className="afl__head">
        <div className="afl__head-text">
          <h1 className="afl__title">Auto Filter</h1>
          <p className="afl__lede">
            {shownLibrary === 'visual-language' ? (
              <>
                {overview?.model ?? 'The model'} tags Visual Language pictures in its one category, Other, from the
                library's own tags. Pictures already tagged by hand are left alone, and no tag a picture already has
                is ever taken off or changed -- a report only adds. New names wait until {overview?.thresholds.other ?? 10} pictures
                carry them.
              </>
            ) : works ? (
              <>
                {overview?.model ?? 'The model'} tags Design References pictures by format, with the Audit library's format
                tags. Pictures from Egnyte are left out: their Format, Product and Type are given by hand. A format
                tagged by hand is left alone, and no tag a picture already has is ever taken off or changed. New format names wait until {overview?.thresholds.format ?? 20} pictures
                carry them.
              </>
            ) : (
              <>
                {overview?.model ?? 'The model'} tags Audit pictures by format, material, brand, product and industry.
                Categories you tagged by hand are left alone, and no tag a picture already has is ever taken off or changed -- a report only adds. Non-Packaging pictures are left out. New names wait until
                enough pictures carry them (brand {overview?.thresholds.brand ?? 5}, others {overview?.thresholds.format ?? 20}).
              </>
            )}
          </p>
        </div>
        <div className="afl__seg afl__switch" role="group" aria-label="Which library">
          {(['audit', 'design-works', 'visual-language'] as const).map((one) => (
            <button
              key={one}
              type="button"
              aria-pressed={shownLibrary === one}
              disabled={running}
              onClick={() => setLibrary(one)}
            >
              {LIBRARY_NAMES[one]}
            </button>
          ))}
        </div>
      </header>

      {error ? <p className="afl__error">{error}</p> : null}

      {/* Picture descriptions (2026-11-03): what each picture shows, for search. */}
      <AutoFilterDescribe library={shownLibrary} />

      <div className="afl__top">
        <section className="afl__card">
          <span className="afl__label">Run</span>
          {tagsOff ? (
            <p className="afl__off">
              AI tagging is off: tags are written by people now, and what a picture shows goes into its description
              above. docs/ai-tags-on.sql turns it back on.
            </p>
          ) : null}
          <div className="afl__stats">
            <div>
              <strong>{overview ? overview.eligible.toLocaleString() : '…'}</strong>
              <span>{name} pictures</span>
            </div>
            <div>
              <strong>{overview ? overview.filtered.toLocaleString() : '…'}</strong>
              <span>filtered</span>
            </div>
            <div>
              <strong>{overview ? notYet.toLocaleString() : '…'}</strong>
              <span>not yet</span>
            </div>
            {overview && overview.failed > 0 ? (
              <div>
                <strong>{overview.failed.toLocaleString()}</strong>
                <span>failed last time</span>
              </div>
            ) : null}
          </div>
          <div className="afl__controls">
            <div className="afl__field">
              <span>Which pictures</span>
              <div className="afl__seg" role="group" aria-label="Which pictures">
                <button type="button" aria-pressed={scope === 'unfiltered'} disabled={running} onClick={() => setScope('unfiltered')}>
                  Not filtered yet
                </button>
                <button type="button" aria-pressed={scope === 'all'} disabled={running} onClick={() => setScope('all')}>
                  All again
                </button>
              </div>
            </div>
            <span className="afl__spacer" />
            {running ? (
              <button type="button" className="afl__btn" onClick={() => (stop.current = true)}>
                Stop
              </button>
            ) : (
              <>
                <button type="button" className="afl__btn" disabled={!overview || tagsOff} onClick={() => void run(50)}>
                  Try 50
                </button>
                <button type="button" className="afl__btn afl__btn--go" disabled={!overview || tagsOff} onClick={() => void run(null)}>
                  Run
                </button>
              </>
            )}
          </div>
          {overview && !running && !tagsOff ? (
            <p className="afl__note">
              {scope === 'all' ? overview.eligible.toLocaleString() : notYet.toLocaleString()} pictures · about{' '}
              {usd((scope === 'all' ? overview.eligible : notYet) * 0.00028)} · Keep this page open while it runs.
            </p>
          ) : null}
          {progress ? (
            <>
              <div className="afl__progress">
                <div className="afl__track">
                  <span style={{ width: `${progress.total ? Math.min(100, (handledCount / progress.total) * 100) : 0}%` }} />
                </div>
                <span>
                  {handledCount.toLocaleString()} of {progress.total.toLocaleString()} · {usd(progress.costUsd)}
                </span>
              </div>
              <p className="afl__note">
                {progress.added.toLocaleString()} tags written · {progress.failed} failed
                {progress.skipped ? ` · ${progress.skipped} skipped` : ''}
                {running && perMinute > 0
                  ? ` · ${Math.round(perMinute)} a minute · about ${left < 1 ? 'a minute' : `${Math.ceil(left)} min`} left`
                  : ''}
              </p>
            </>
          ) : null}
        </section>

        <section className="afl__card">
          <span className="afl__label">Latest runs</span>
          {overview && overview.runs.length > 0 ? (
            <div className="afl__runs">
              {overview.runs.map((one) => (
                <RunRow key={one.id} run={one} busy={running} onUndone={reload} />
              ))}
            </div>
          ) : (
            <p className="afl__quiet">{overview ? 'No runs yet.' : 'Reading…'}</p>
          )}
        </section>
      </div>

      {/* Reported pictures and the Trash moved to the Review Queue (Huanrui, 2026-10-15). */}
      <ReviewLink />

      <PendingBoard overview={overview} library={shownLibrary} onChanged={reload} />


      {shownLibrary !== 'audit' ? null : (
        <details className="afl__card afl__fold">
          <summary className="afl__label">Pilot: compare with your own tags, nothing written</summary>
          <AutoFilterPilot />
        </details>
      )}
    </div>
  )
}

function RunRow({ run, busy, onUndone }: { run: FilterOverview['runs'][number]; busy: boolean; onUndone: () => void }) {
  const [asking, setAsking] = useState(false)
  const [working, setWorking] = useState(false)
  // A report runs on the server, so "running" is true of it; a page-driven
  // run still "running" was left when its tab closed.
  const status =
    run.status === 'undone'
      ? 'undone'
      : run.status === 'running'
        ? run.scope === 'report'
          ? 'tagging…'
          : 'stopped early'
        : run.status
  return (
    <div className={`afl__run${run.status === 'undone' ? ' afl__run--undone' : ''}`}>
      <span>{when(run.created_at)}</span>
      <span>
        {run.scope === 'report' ? `Reported by ${run.started_by.split('@')[0]} · ` : ''}
        {run.done.toLocaleString()}
        {run.scope === 'report' ? ` of ${run.total}` : ''} pictures{run.failed ? ` · ${run.failed} failed` : ''} ·{' '}
        {usd(Number(run.cost_usd) || 0)}
      </span>
      <em>{status}</em>
      {run.status !== 'undone' ? (
        <button
          type="button"
          className={`afl__mini${asking ? ' afl__mini--warn' : ''}`}
          disabled={busy || working}
          onClick={() => {
            if (!asking) {
              setAsking(true)
              window.setTimeout(() => setAsking(false), 4000)
              return
            }
            setWorking(true)
            void undoFilterRun(run.id)
              .then(onUndone)
              .finally(() => setWorking(false))
          }}
        >
          {working ? 'Undoing…' : asking ? 'Undo? Click again' : 'Undo'}
        </button>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Pending tags, foldable (Huanrui, 2026-10-08: the list had grown too long
   to scroll past)
   ------------------------------------------------------------------------- */

function PendingBoard({ overview, library, onChanged }: { overview: FilterOverview | null; library: FilterLibrary; onChanged: () => void }) {
  const [open, setOpen] = useRemembered<boolean>(`ww.autoFilter.pending.${library}`, false)
  if (!overview) return null
  // Type is only ever Sketch or Render: nothing of it waits.
  const kinds = (overview.kinds?.length ? overview.kinds : (['format', 'material', 'brand', 'product', 'industry'] as FilterKind[])).filter(
    (kind) => kind !== 'type',
  )
  const counts = kinds.map((kind) => ({ kind, count: overview.pending.filter((tag) => tag.kind === kind).length }))
  const total = counts.reduce((sum, one) => sum + one.count, 0)
  return (
    <details className="afl__card afl__fold" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="afl__label">
        Pending tags · {total.toLocaleString()} waiting
        {kinds.length > 1 ? (
          <em className="afl__summary-note">{counts.map((one) => `${LABELS[one.kind]} ${one.count}`).join(' · ')}</em>
        ) : null}
      </summary>
      <p className="afl__note afl__note--top">They join the library at their threshold.</p>
      <div className={`afl__pending${kinds.length === 1 ? ' afl__pending--single' : ''}`}>
        {kinds.map((kind) => (
          <PendingColumn key={kind} kind={kind} overview={overview} library={library} onChanged={onChanged} />
        ))}
      </div>
    </details>
  )
}

function PendingColumn({
  kind,
  overview,
  library,
  onChanged,
}: {
  kind: FilterKind
  overview: FilterOverview
  library: FilterLibrary
  onChanged: () => void
}) {
  const [all, setAll] = useState(false)
  const tags = useMemo(
    () => overview.pending.filter((tag) => tag.kind === kind).sort((a, b) => b.count - a.count),
    [overview.pending, kind],
  )
  const ready = tags.filter((tag) => tag.count >= overview.thresholds[kind]).length
  const shown = all ? tags : tags.slice(0, COLUMN_FIRST)
  return (
    <div className="afl__pcol">
      <div className="afl__pcol-head">
        <span className="afl__pcol-name">
          {LABELS[kind]} <em>{tags.length}</em>
        </span>
        {ready > 0 ? <ApproveAll kind={kind} library={library} count={ready} onDone={onChanged} /> : null}
      </div>
      {tags.length === 0 ? <p className="afl__quiet">None waiting.</p> : null}
      <div className="afl__pcol-cards">
        {shown.map((tag) => (
          <PendingCard key={tag.id} tag={tag} threshold={overview.thresholds[kind]} existing={overview.tags[kind]} onChanged={onChanged} />
        ))}
      </div>
      {tags.length > COLUMN_FIRST ? (
        <button type="button" className="afl__mini afl__more" onClick={() => setAll((now) => !now)}>
          {all ? 'Show fewer' : `Show all ${tags.length}`}
        </button>
      ) : null}
    </div>
  )
}

/** The tags of a column that have reached their threshold, at once; a second
 *  click to be sure. Tags under it keep waiting (Huanrui, 2026-10-06). */
function ApproveAll({ kind, library, count, onDone }: { kind: FilterKind; library: FilterLibrary; count: number; onDone: () => void }) {
  const [asking, setAsking] = useState(false)
  const [working, setWorking] = useState(false)
  return (
    <button
      type="button"
      className="afl__mini"
      disabled={working}
      onClick={() => {
        if (!asking) {
          setAsking(true)
          window.setTimeout(() => setAsking(false), 4000)
          return
        }
        setWorking(true)
        void approveAllPending(kind, library)
          .then(onDone)
          .finally(() => {
            setWorking(false)
            setAsking(false)
          })
      }}
    >
      {working ? 'Approving…' : asking ? `Approve ${count} ready? Click again` : `Approve ${count} ready`}
    </button>
  )
}

function PendingCard({
  tag,
  threshold,
  existing,
  onChanged,
}: {
  tag: PendingTag
  threshold: number
  existing: { id: string; name: string }[]
  onChanged: () => void
}) {
  const [mode, setMode] = useState<'idle' | 'rename' | 'merge'>('idle')
  const [name, setName] = useState(tag.name)
  const [into, setInto] = useState('')
  const [error, setError] = useState('')
  const [working, setWorking] = useState(false)

  const act = (action: 'approve' | 'reject' | 'rename' | 'merge', extra: { name?: string; intoId?: string } = {}) => {
    setWorking(true)
    setError('')
    actOnPendingTag(tag.id, action, extra)
      .then(onChanged)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That did not work'))
      .finally(() => setWorking(false))
  }

  return (
    <div className="afl__ptag">
      <div className="afl__ptag-head">
        <strong>{tag.name}</strong>
        <span>
          {tag.count}/{threshold}
        </span>
      </div>
      <div className="afl__track afl__track--thin">
        <span style={{ width: `${Math.min(100, (tag.count / threshold) * 100)}%` }} />
      </div>
      {tag.samples.length > 0 ? (
        <div className="afl__samples">
          {tag.samples.map((src) => (
            <img key={src} src={src} alt="" loading="lazy" />
          ))}
        </div>
      ) : null}
      {mode === 'rename' ? (
        <div className="afl__inline">
          <input value={name} onChange={(event) => setName(event.target.value)} aria-label="New name" />
          <button type="button" className="afl__mini" disabled={working || !name.trim()} onClick={() => act('rename', { name })}>
            Save
          </button>
          <button type="button" className="afl__mini" onClick={() => setMode('idle')}>
            Cancel
          </button>
        </div>
      ) : mode === 'merge' ? (
        <div className="afl__inline">
          <select value={into} onChange={(event) => setInto(event.target.value)} aria-label="Merge into">
            <option value="">Merge into…</option>
            {existing.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
          <button type="button" className="afl__mini" disabled={working || !into} onClick={() => act('merge', { intoId: into })}>
            Merge
          </button>
          <button type="button" className="afl__mini" onClick={() => setMode('idle')}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="afl__inline">
          <button type="button" className="afl__mini" disabled={working} onClick={() => act('approve')}>
            Approve
          </button>
          <button type="button" className="afl__mini" disabled={working} onClick={() => setMode('rename')}>
            Rename
          </button>
          <button type="button" className="afl__mini" disabled={working || existing.length === 0} onClick={() => setMode('merge')}>
            Merge
          </button>
          <button type="button" className="afl__mini afl__mini--warn" disabled={working} onClick={() => act('reject')}>
            Reject
          </button>
        </div>
      )}
      {error ? <p className="afl__error">{error}</p> : null}
    </div>
  )
}

/** Where Reported and the Trash went: one line, with how many wait there. */
function ReviewLink() {
  const [counts, setCounts] = useState<{ reported: number; duplicates: number } | null>(null)
  useEffect(() => {
    readReviewCounts()
      .then(setCounts)
      .catch(() => setCounts(null))
  }, [])
  const waiting = counts ? counts.reported + counts.duplicates : 0
  return (
    <p className="afl__card afl__review-link">
      {counts && waiting > 0
        ? `${counts.reported} reported · ${counts.duplicates} possible duplicates — `
        : 'Reported pictures, possible duplicates and the Trash are in '}
      <Link to="/review">{counts && waiting > 0 ? 'open the Review Queue' : 'the Review Queue'}</Link>
    </p>
  )
}
